import { ReactNode } from 'react';
import { patternOnsetBeats, RhythmInput } from '../../../../../common/src/rhythm_echo.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import { TextButton } from '../learn_buttons.tsx';
import { allLights, lightsForPlayback, RhythmBlocks } from './rhythm_blocks.tsx';
import { RhythmCueRow } from './rhythm_cue_row.tsx';
import {
  cueNow,
  isTapButtonOpen,
  playbackOnsetIndex,
  RhythmPatternPhase,
  RhythmPatternState,
  tapsAccepted,
} from './rhythm_echo_session.ts';
import { RhythmNotation } from './rhythm_notation.tsx';
import {
  BlocksRow,
  CueSlack,
  FootRow,
  HintRow,
  NotationRow,
  PatternProgressRow,
  ScreenBody,
  ScreenSubtitle,
  ScreenTitle,
  VoiceMeterRow,
} from './rhythm_screen_parts.tsx';
import { rhythmSpaceBarHint, RhythmTapButton } from './rhythm_tap_button.tsx';
import { RhythmTapNotation } from './rhythm_tap_notation.tsx';

export const rhythmPatternScreenId = 'rhythm-pattern';
export const rhythmStopButtonId = 'rhythm-stop-button';

// The two phases of one pattern on one screen (issue #3473, re-paced in #3519 and #3588), from the
// "Rhythm Echo (listen)" artboard: the pattern row, a title and line per phase, the syllable blocks
// (all off through the silent lead beat, filling in as the bar plays, then fully lit while the
// singer taps it back), the notation row (the pattern's notes; in the tap phase the tap notation
// instead, the same height, filling in with the singer's taps against ghosts of the expected notes
// as they land), the Ready · Set · Go! cue row filling the space where the beat dots used to be
// (one big silent word pulsing per beat: Ready and Set over the play bar's last two beats, Go!
// landing on the first tap), the tap button (visible from the start, looking closed with "on Go!"
// until Go!, when it opens with "now" - though the page already keeps taps from Set, a beat
// earlier, see isTapButtonOpen and isTapOpen) and, in the tap phase, Stop in the foot row every
// phase reserves. The rows
// are the ones the warm-up is built from too (rhythm_screen_parts.tsx), so nothing moves when it
// hands over to Pattern 1. Presentational: everything comes from the reducer state through its
// selectors, so a story can render any phase from a state it builds by hand.
//
// Sing Rhythm (issue #3723) is the same screen with `voiceMeter`: the singer sings the pattern back
// instead of tapping it, so the meter showing their voice stands where the tap button was, the cue
// word above it is set larger in the room the button gave up, and the copy says sing.
export const RhythmPatternScreen = ({
  state,
  onTap,
  onStop,
  voiceMeter,
}: {
  state: RhythmPatternState;
  onTap: (perfMs: number) => void;
  onStop: () => void;
  voiceMeter?: ReactNode; // the "Hearing you" meter, on a screen the singer sings to
}) => {
  const input: RhythmInput = voiceMeter === undefined ? 'tap' : 'voice';
  const pattern = state.patterns[state.patternIndex];
  const onsetCount = patternOnsetBeats(pattern).length;
  const { phase } = state;
  const open = isTapButtonOpen(state); // the look only: the page gates taps on isTapOpen, from Set
  const lights =
    phase === 'listen' ? lightsForPlayback(onsetCount, playbackOnsetIndex(state)) : allLights(onsetCount, 'on');
  return (
    <ScreenBody id={rhythmPatternScreenId} data-phase={phase} data-input={input}>
      <PatternProgressRow index={state.patternIndex} count={state.patterns.length} level={state.level} />
      <ScreenTitle>{phaseTitle(phase)}</ScreenTitle>
      <ScreenSubtitle>{phaseSubtitle(phase, input)}</ScreenSubtitle>
      <BlocksRow>
        <RhythmBlocks pattern={pattern} lights={lights} />
      </BlocksRow>
      <NotationRow>
        {phase === 'tap' ? (
          <RhythmTapNotation
            pattern={pattern}
            level={state.level}
            tapsMs={tapsAccepted(state).map((tapMs) => tapMs - state.timeline.tapBarStartMs)}
            nowMs={state.nowMs - state.timeline.tapBarStartMs}
          />
        ) : (
          <RhythmNotation pattern={pattern} />
        )}
      </NotationRow>
      <CueSlack>
        <RhythmCueRow cue={cueNow(state)} large={input === 'voice'} />
      </CueSlack>
      {input === 'voice' ? (
        <VoiceMeterRow>{voiceMeter}</VoiceMeterRow>
      ) : (
        <RhythmTapButton open={open} caption={open ? 'now' : 'on Go!'} onTap={onTap} />
      )}
      <HintRow>{input === 'voice' ? '' : rhythmSpaceBarHint}</HintRow>
      <FootRow>
        {phase === 'tap' && (
          <TextButton
            id={rhythmStopButtonId}
            data-testid={rhythmStopButtonId}
            tabIndex={-1}
            onPointerDown={(event) => event.preventDefault()} // never focused, so the space bar cannot press it
            onClick={onStop}
          >
            Stop
          </TextButton>
        )}
      </FootRow>
    </ScreenBody>
  );
};

export function phaseTitle(phase: RhythmPatternPhase): string {
  switch (phase) {
    case 'listen':
      return 'Listen';
    case 'tap':
      return 'Your turn';
    default:
      return ensureUnreachable(phase);
  }
}

export function phaseSubtitle(phase: RhythmPatternPhase, input: RhythmInput = 'tap'): string {
  switch (phase) {
    case 'listen':
      return input === 'voice' ? 'Then sing it back.' : 'Then tap it back.';
    case 'tap':
      return input === 'voice' ? 'Sing it back now.' : 'Tap it back now.';
    default:
      return ensureUnreachable(phase);
  }
}
